import { useState } from "react";
import { useT } from "@transifex/react";
import { useHistory } from "react-router-dom";
import type { ClassActivity } from "../complex/classActivityCard/ClassActivityCardsOverview.types";
import type { GetBandInfoResponse, UnitCard, UserOwnCard, UserSubject } from "p6m-p6u";
import { ClassActivityCategory } from "../constants/ClassActivityConstants";
import { CreationModes } from "../constants/CreationFlowConstants";
import { useGeneralContext } from "../context/AppGeneralContext";
import { useBookContext } from "../context/BookContext";
import { useTestCreationContext } from "../context/TestCreationContext";
import { AddCardResults } from "../enums/cardSelection";
import { SourceType } from "../enums/sources";
import {
    ActivitySourceKinds,
    loadCardsFromTest,
    fetchOwnContentCards,
    getTrainingCardIds,
    normalizeBookCards,
} from "../helpers/ExistingActivitySelection";
import { getRequest, sortAndFilterUnitsForBandResponse } from "../helpers/networkHelper";
import { resolveArticleUuid } from "../helpers/UserExercises";
import { getCardsByIds } from "../networking/tests";

interface ResolvedActivitySource {
    kind: ActivitySourceKinds;
    articleId?: string;
    articleIdIsUuid?: boolean;
    ownSubjectId?: string;
}

/** Everything needed to switch the selection, fetched before any state is touched. */
interface PreparedSelection {
    source: ResolvedActivitySource;
    cards: Array<UnitCard | UserOwnCard>;
    bandData?: GetBandInfoResponse;
    userSubject?: UserSubject;
}

// A mixed test (book + own words) resolves to the book, matching the test flow's source-tile order.
const _resolveSource = (activity: ClassActivity, userId: string): ResolvedActivitySource | undefined => {
    if (activity.category === ClassActivityCategory.TEST) {
        const test = activity.testData;
        if (test.articleId) {
            return {
                kind: ActivitySourceKinds.BOOK,
                articleId: `${test.articleId}`,
                ownSubjectId: test.ownSubjectInfo?.id,
            };
        }
        if (test.ownSubjectInfo?.id) {
            return { kind: ActivitySourceKinds.OWN, ownSubjectId: test.ownSubjectInfo.id };
        }
        return undefined;
    }

    const subjectContentId = activity.vocabTrainingData.subjectContentId?.id;
    if (!subjectContentId) return undefined;
    const articleUuid = resolveArticleUuid(activity.vocabTrainingData, userId);
    if (!articleUuid) {
        return { kind: ActivitySourceKinds.OWN, ownSubjectId: subjectContentId };
    }
    return { kind: ActivitySourceKinds.BOOK, articleId: articleUuid, articleIdIsUuid: true };
};

export const useExistingActivitySelection = () => {
    const {
        setDrawerContentFromCards,
        loadInitArticleIdData,
        loadUserOwnSubjectUUIDsInfoIntoState,
        setSelectedUserSubject,
        setSelectedBookData,
        setShouldSubjectSelectionBeDisabled,
        clearSchoolbookSelection,
        clearOwnContentSelection,
        openWarningModal,
        maxAmountOfCardsSelectable,
        hasConfirmedLargeVocabTraining,
        setHasConfirmedLargeVocabTraining,
    } = useTestCreationContext();
    const { setLastSourceSelected, isBookBlocked } = useBookContext();
    const { creatingMode, makePxpRequest, setIsDataBeingLoaded, userId } = useGeneralContext();
    const history = useHistory();
    const t = useT();

    const [isLoadingActivity, setIsLoadingActivity] = useState(false);
    const [pendingLargeSelection, setPendingLargeSelection] = useState<PreparedSelection | undefined>();

    const isRtFlow = creatingMode === CreationModes.RECURRING_TASK;

    const t_loadError = t("The vocabulary of this activity could not be loaded.", {
        _tags: "CreateTest,ExistingActivityContent",
    });
    const t_bookNotAvailable = t("This book is not available for this activity.", {
        _tags: "CreateTest,ExistingActivityContent",
    });
    const t_limitReached = t(
        "The activity contains more words than can be selected. The first {maxAmountOfCards} were selected.",
        { maxAmountOfCards: maxAmountOfCardsSelectable, _tags: "CreateTest,ExistingActivityContent" }
    );

    const _fetchBandData = (source: ResolvedActivitySource) =>
        loadInitArticleIdData(source.articleId!, !!source.articleIdIsUuid, false);

    const _getCards = async (
        activity: ClassActivity,
        source: ResolvedActivitySource
    ): Promise<Pick<PreparedSelection, "cards" | "bandData">> => {
        if (activity.category === ClassActivityCategory.TEST) {
            // The vocab-training flow keeps sources exclusive, so a mixed test contributes only
            // its primary source's words there.
            const filter = isRtFlow ? source.kind : undefined;
            return { cards: await loadCardsFromTest(activity.testData, filter) };
        }

        const cardIds = getTrainingCardIds(activity.vocabTrainingData);
        if (source.kind === ActivitySourceKinds.OWN) {
            return { cards: await fetchOwnContentCards(makePxpRequest, source.ownSubjectId!, new Set(cardIds)) };
        }

        const cardsResponse = await getCardsByIds(cardIds);
        if (!cardsResponse.items?.length) {
            return { cards: [] };
        }
        const bandData = await _fetchBandData(source);
        const unitsResponse = await getRequest("p6u/get-units-for-band/", { bandId: bandData.band?.ID });
        const units = sortAndFilterUnitsForBandResponse(unitsResponse?.data ?? []);
        return { cards: normalizeBookCards(cardsResponse.items || [], units), bandData };
    };

    /**
     * Undefined when the activity has no usable cards (warned here), comes from a book the current
     * flow excludes (warned here) or its own subject failed to load (warned by the context).
     */
    const _prepareSelection = async (
        activity: ClassActivity,
        source: ResolvedActivitySource
    ): Promise<PreparedSelection | undefined> => {
        const { cards, bandData: bandDataFromCards } = await _getCards(activity, source);
        if (cards.length === 0) {
            openWarningModal(t_loadError);
            return undefined;
        }

        const isBookSource = source.kind === ActivitySourceKinds.BOOK;
        const bandData = isBookSource && !bandDataFromCards ? await _fetchBandData(source) : bandDataFromCards;
        // Backstop for the books the list can't filter out itself, and the last point before any
        // creation state is written — nothing above this touches it.
        if (isBookBlocked(bandData)) {
            openWarningModal(t_bookNotAvailable);
            return undefined;
        }

        let userSubject: UserSubject | undefined;
        const skipsOwnSubject = isRtFlow && isBookSource;
        if (source.ownSubjectId && !skipsOwnSubject) {
            const subjectsInfo = await loadUserOwnSubjectUUIDsInfoIntoState([source.ownSubjectId]);
            userSubject = subjectsInfo?.[source.ownSubjectId];
            if (!userSubject) return undefined;
        }

        return { source, cards, bandData, userSubject };
    };

    /** Sole mutator of the creation state; synchronous and never fails. */
    const _applySelection = (prepared: PreparedSelection) => {
        const isBookSource = prepared.source.kind === ActivitySourceKinds.BOOK;
        // Same as the vocab-training source page: switching source drops the other one.
        if (isRtFlow) {
            if (isBookSource) {
                clearOwnContentSelection();
            } else {
                clearSchoolbookSelection();
            }
        }

        if (prepared.bandData) {
            setSelectedBookData({ ...prepared.bandData });
        }
        if (prepared.userSubject) {
            setSelectedUserSubject(prepared.userSubject);
            setShouldSubjectSelectionBeDisabled(true);
        }
        setLastSourceSelected(isBookSource ? SourceType.BOOK : SourceType.OWN);

        const addResult = setDrawerContentFromCards(prepared.cards);
        if (addResult === AddCardResults.INCOMPLETE_ADD) {
            openWarningModal(t_limitReached);
        }

        history.push(isRtFlow && !isBookSource ? "/create/select-own-source" : "/create/select-vocabulary");
    };

    const loadActivityIntoSelection = async (activity: ClassActivity): Promise<boolean> => {
        const source = _resolveSource(activity, userId);
        if (!source) {
            openWarningModal(t_loadError);
            return false;
        }

        setIsLoadingActivity(true);
        setIsDataBeingLoaded(true);
        try {
            const prepared = await _prepareSelection(activity, source);
            if (!prepared) {
                return false;
            }

            const needsConfirmation =
                isRtFlow && prepared.cards.length > maxAmountOfCardsSelectable && !hasConfirmedLargeVocabTraining;
            if (needsConfirmation) {
                setPendingLargeSelection(prepared);
                return true;
            }

            _applySelection(prepared);
            return true;
        } catch (e) {
            console.error("Failed to load the activity into the selection", e);
            openWarningModal(t_loadError);
            return false;
        } finally {
            setIsDataBeingLoaded(false);
            setIsLoadingActivity(false);
        }
    };

    const confirmLargeSelection = () => {
        if (!pendingLargeSelection) return;
        setHasConfirmedLargeVocabTraining(true);
        _applySelection(pendingLargeSelection);
        setPendingLargeSelection(undefined);
    };

    const cancelLargeSelection = () => {
        setPendingLargeSelection(undefined);
    };

    return {
        loadActivityIntoSelection,
        isLoadingActivity,
        hasPendingLargeSelection: !!pendingLargeSelection,
        confirmLargeSelection,
        cancelLargeSelection,
    };
};
